/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import axios from 'axios';
import {
  API_BASE,
  withAntiBotRetry as _withAntiBotRetry,
} from '@features/property-management/components/publications-center/api';
import type { PublicationsCatalogResponse } from '../types';

export { isAntiBotError } from '@shared/utils/with-antibot-retry';

/**
 * Wraps a request function with anti-bot challenge retry handling.
 *
 * @param fn - Request function receiving anti-bot headers.
 * @returns The request result once it succeeds (after any retry).
 */
export function withAntiBotRetry<T>(
  fn: (headers: Record<string, string>) => Promise<T>,
): Promise<T> {
  return _withAntiBotRetry(fn);
}

/**
 * Fetches all portal publications for an agency.
 *
 * @param agencyId - Agency scope.
 * @returns The publications catalog response.
 */
export async function fetchAgencyPublications(
  agencyId: string,
): Promise<PublicationsCatalogResponse> {
  const res = await withAntiBotRetry((headers) =>
    axios.get(`${API_BASE}/portal-integration/agencies/${agencyId}/publications`, { headers }),
  );
  return res.data as PublicationsCatalogResponse;
}

/**
 * Removes a property's publication from a specific portal.
 *
 * @param agencyId - Agency scope.
 * @param propertyId - Property whose publication is removed.
 * @param portal - Target portal identifier.
 * @param opts - Optional portal-specific post/listing identifiers.
 */
export async function removePublication(
  agencyId: string,
  propertyId: string,
  portal: string,
  opts: { post_id?: string; listing_id?: string } = {},
): Promise<void> {
  await withAntiBotRetry((headers) =>
    axios.delete(
      `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publications/${portal}`,
      { headers, data: opts },
    ),
  );
}

/**
 * Updates a property's publication payload on a specific portal.
 *
 * @param agencyId - Agency scope.
 * @param propertyId - Property whose publication is updated.
 * @param portal - Target portal identifier.
 * @param body - Portal-specific update payload.
 */
export async function updatePublication(
  agencyId: string,
  propertyId: string,
  portal: string,
  body: Record<string, unknown>,
): Promise<void> {
  await withAntiBotRetry((headers) =>
    axios.put(
      `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publications/${portal}`,
      body,
      { headers },
    ),
  );
}
